import { computed, mergeProps, reactive, toValue, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { camelCase } from 'lodash';

import { SpotType } from '@nova/core';
import { nestedAreaPresenterDataProps } from '@satellite/composables/presenterNestedAreaProps';
import { EntityPresenterMode, isStaticMode } from '@satellite/composables/presenterEntityMode';
import { reactiveOption } from '@satellite/composables/presenterReactiveOption';

import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import type { Maybe } from '@nova/core';
import type {
  PresenterDisplayConfigOptions,
  PresenterDisplayOverrideProps,
  PresenterDisplaySize
} from '@satellite/composables/presenterDisplayOptions';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterPlaceholder';
import type { YardAreaDetails } from '@satellite/composables/useYardAreaPresenter';
import type { YardSpotDetails } from './yardSpotPresenterShared';

export type YardSpotDisplayField = 'area' | 'code' | 'spotIcon' | 'spotStatus';

export interface UseYardSpotDisplayOptions extends PresenterDisplayConfigOptions {
  area?: Ref<YardAreaDetails | null> | ComputedRef<YardAreaDetails | null>;
  isAreaFetching?: Ref<boolean> | ComputedRef<boolean>;
  initialAreaData?: MaybeRefOrGetter<Maybe<YardAreaDetails>>;
  placeholderAreaData?: MaybeRefOrGetter<Maybe<PresenterPlaceholderData<YardAreaDetails>>>;
  staticAreaData?: MaybeRefOrGetter<Maybe<YardAreaDetails>>;
  areaProps?: PresenterDisplayOverrideProps;
  codeProps?: PresenterDisplayOverrideProps;
  spotIconProps?: PresenterDisplayOverrideProps;
  spotStatusProps?: PresenterDisplayOverrideProps;
}

function resolveSpotIcon(type: Maybe<SpotType>): string {
  return type === SpotType.DOCKING ? 'mdi-truck-outline' : 'mdi-parking';
}

export function useYardSpotDisplay(
  spot: Ref<YardSpotDetails | null> | ComputedRef<YardSpotDetails | null>,
  options: UseYardSpotDisplayOptions = {}
) {
  const { t } = useI18n();

  const displaySize = reactiveOption<PresenterDisplaySize>(options.size, 'regular');
  const displayMode = reactiveOption(options.mode, EntityPresenterMode.Live);
  const isFetchingValue = computed(() => (options.isFetching ? unref(options.isFetching) : false));
  const isAreaFetchingValue = computed(() =>
    options.isAreaFetching ? unref(options.isAreaFetching) : false
  );

  const areaId = computed(() => spot.value?.spotAreaId ?? null);
  const code = computed(() => spot.value?.code ?? null);
  const spotType = computed(() => spot.value?.type ?? null);
  const spotIcon = computed(() => resolveSpotIcon(spotType.value));
  const spotStatus = computed(() => {
    if (!spot.value) {
      return null;
    }
    const status = spot.value.status;
    return t(`core.yard.spotStatuses.${camelCase(status)}`);
  });

  const resolvedArea = computed((): PresenterPlaceholderData<YardAreaDetails> | null => {
    if (options.area) {
      return options.area.value;
    }

    if (isStaticMode(displayMode.value)) {
      return toValue(options.staticAreaData) ?? null;
    }

    return toValue(options.placeholderAreaData) ?? toValue(options.initialAreaData) ?? null;
  });

  const areaName = computed(() => resolvedArea.value?.name ?? null);

  const areaPresenterProps = computed(() =>
    mergeProps(
      nestedAreaPresenterDataProps(displayMode.value, {
        area:
          toValue(options.staticAreaData) ??
          toValue(options.placeholderAreaData) ??
          toValue(options.initialAreaData)
      }),
      toValue(options.areaProps) ?? {}
    )
  );

  const codeAttrs = computed(() =>
    mergeProps({ 'data-testid': 'yard-spot-presenter-spot-code' }, toValue(options.codeProps) ?? {})
  );

  const spotStatusAttrs = computed(() =>
    mergeProps(
      {
        class: 'd-flex flex-row align-center typography-chip-sm spot-status text-uppercase',
        'data-testid': 'yard-spot-presenter-spot-status'
      },
      toValue(options.spotStatusProps) ?? {}
    )
  );

  const areaNameAttrs = computed(() =>
    mergeProps({ 'data-testid': 'yard-area-presenter-area-name' }, toValue(options.areaProps) ?? {})
  );

  const spotIconAttrs = computed(() =>
    mergeProps(
      { icon: spotIcon.value, 'data-testid': 'yard-spot-presenter-spot-icon' },
      toValue(options.spotIconProps) ?? {}
    )
  );

  return reactive({
    spot,
    area: resolvedArea,
    mode: displayMode,
    size: displaySize,
    isFetching: isFetchingValue,
    isAreaFetching: isAreaFetchingValue,
    areaId,
    code,
    spotType,
    spotIcon,
    spotStatus,
    areaName,
    areaPresenterProps,
    codeAttrs,
    spotStatusAttrs,
    areaNameAttrs,
    spotIconAttrs
  });
}

export type YardSpotDisplayData = ReturnType<typeof useYardSpotDisplay>;
